import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { sig } from '../engine/normalize';

const RT_SOURCE = 'https://www.w3.org/TR/resource-timing/';

/**
 * Network Information API + a Resource Timing fetch probe.
 *
 * nextHopProtocol and the connection-reuse profile are worth noting: a browser
 * on a fresh connection reports h2/h3 and does a full TLS handshake; a
 * reused connection shows connectionReuse in the timing entry. These feed the
 * network part but are not scored on their own (a slow phone is not a bot).
 */
export const networkProbe: Probe = {
  id: 'network',
  label: 'Network',
  layer: 'network',
  tier: 'client',
  category: 'network',
  description: 'Network Information API and connection timing profile',
  budgetMs: 8000,

  async run(): Promise<ProbeResult> {
    const conn = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;

    const networkInfo = conn
      ? {
          effectiveType: conn.effectiveType ?? null,
          downlink: conn.downlink ?? null,
          rtt: conn.rtt ?? null,
          saveData: conn.saveData ?? null,
          type: conn.type ?? null,
          effectiveConnectionTypePresent: true,
        }
      : null;

    // Timing probe. We fetch a same-origin asset and read the Resource Timing
    // entry. `nextHopProtocol` is the most valuable field: it is negotiated
    // below JS and reflects H1 vs H2 vs H3.
    const timing = await probeTiming();

    const findings: Finding[] = [];
    if (timing && timing.nextHopProtocol === 'http/1.1' && !isOldBrowser()) {
      // H1 from a modern browser can be legitimate (server config, some
      // proxies), so this is low severity with no score — surfaced for context.
      findings.push({
        id: 'network.h1-from-modern-browser',
        severity: 'info',
        category: 'network',
        layer: 'network',
        confidence: 'low',
        title: 'Connection negotiated HTTP/1.1',
        detail:
          'Modern browsers prefer HTTP/2 or HTTP/3. HTTP/1.1 can be legitimate (server configuration, middleboxes, proxies), so this is informational and contributes no score.',
        sources: [RT_SOURCE],
        evidence: [`nextHopProtocol=http/1.1`],
        scoreContribution: 0,
      });
    }

    return {
      signals: {
        networkInformation: networkInfo ? ok(networkInfo) : na('unsupported', 'client', 'Network Information API absent'),
        fetchOk: timing ? ok(timing.ok, 'client') : na<boolean>('unavailable', 'client', 'timing probe failed'),
        nextHopProtocol: ok(timing?.nextHopProtocol ?? null, 'client'),
        roundTripMs: ok(timing ? sig(timing.roundTripMs, 3) : null, 'client'),
        dnsMs: ok(timing ? sig(timing.dnsMs, 3) : null, 'client'),
        tcpMs: ok(timing ? sig(timing.tcpMs, 3) : null, 'client'),
        tlsMs: ok(timing ? sig(timing.tlsMs, 3) : null, 'client'),
        ttfbMs: ok(timing ? sig(timing.ttfbMs, 3) : null, 'client'),
        transferSize: ok(timing?.transferSize ?? null, 'client'),
        connectionReused: ok(timing?.connectionReused ?? null, 'client'),
      },
      findings,
    };
  },
};

function isOldBrowser(): boolean {
  const ua = navigator.userAgent;
  return /Chrome\/(\d+)/.test(ua) ? Number(/Chrome\/(\d+)/.exec(ua)?.[1] ?? 999) < 80 : false;
}

interface TimingResult {
  ok: boolean;
  nextHopProtocol: string | null;
  roundTripMs: number;
  dnsMs: number;
  tcpMs: number;
  tlsMs: number;
  ttfbMs: number;
  transferSize: number;
  connectionReused: boolean;
  serverTiming: string | null;
}

async function probeTiming(): Promise<TimingResult | null> {
  // A cache-busting query on a same-origin asset guarantees a network entry.
  const url = `/favicon.svg?_=${Date.now()}`;
  const t0 = performance.now();
  try {
    const res = await fetch(url, { cache: 'no-store' });
    // Drain the body so the timing entry is complete.
    await res.arrayBuffer().catch(() => undefined);
    const roundTripMs = performance.now() - t0;

    // Read the Resource Timing entry. On a reused/second load there is no
    // connect phase, so domainLookup*/connect* are 0 and the deltas are 0.
    const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    const entry = entries.filter((e) => e.name.includes('favicon.svg')).pop() ?? null;

    const delta = (a: number | undefined, b: number | undefined): number => {
      if (a === undefined || b === undefined) return 0;
      return Math.max(0, a - b);
    };

    return {
      ok: res.ok,
      nextHopProtocol: entry?.nextHopProtocol ?? null,
      roundTripMs,
      dnsMs: entry ? delta(entry.domainLookupEnd, entry.domainLookupStart) : 0,
      tcpMs: entry ? delta(entry.connectEnd, entry.connectStart) : 0,
      tlsMs: entry ? delta(entry.connectEnd, entry.secureConnectionStart) : 0,
      ttfbMs: entry ? delta(entry.responseStart, entry.requestStart) : 0,
      transferSize: entry?.transferSize ?? 0,
      connectionReused: entry ? entry.transferSize === 0 : false,
      serverTiming: res.headers.get('server-timing'),
    };
  } catch {
    return null;
  }
}

interface NetworkInformationLike {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  type?: string;
}

registerProbe(networkProbe);
